import { useEffect, useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { Download, KeyRound, LogOut, ShieldCheck, ShieldOff } from 'lucide-react'
import QRCode from 'qrcode'
import { passwordIssues, type LoginResponse } from '@concours/shared'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  InfoGrid,
  InfoItem,
  Input,
  Modal,
  OtpInput,
  PageHeader,
  PasswordInput,
  useAuth,
  useToast,
} from '@concours/ui'
import { errorMessage, fieldErrors } from '../lib/errors'
import { MY_PERMISSIONS_KEY } from '../lib/permissions'

interface MfaStatus {
  enabled: boolean
  required: boolean
  recoveryCodesRemaining: number
}

const dateTime = new Intl.DateTimeFormat('fr-FR', { dateStyle: 'long', timeStyle: 'short' })

export function AccountPage() {
  const { t } = useTranslation()
  const { user } = useAuth()
  if (!user) return null

  return (
    <div className="stack" style={{ gap: 'var(--space-6)' }}>
      <PageHeader
        eyebrow={t('account.eyebrow')}
        title={t('account.title')}
        description={t('account.description')}
      />

      <Card>
        <CardHeader title={t('account.identity')} />
        <CardBody>
          <InfoGrid>
            <InfoItem label={t('account.name')}>
              {[user.firstName, user.lastName].filter(Boolean).join(' ') || '—'}
            </InfoItem>
            <InfoItem label={t('account.email')}>{user.email}</InfoItem>
            <InfoItem label={t('account.lastLogin')}>
              {user.lastLoginAt ? dateTime.format(new Date(user.lastLoginAt)) : '—'}
            </InfoItem>
          </InfoGrid>
        </CardBody>
      </Card>

      <MfaCard />
      <ChangePasswordCard email={user.email} />
      <SessionsCard />
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Double authentification                                             */
/* ------------------------------------------------------------------ */

function MfaCard() {
  const { t } = useTranslation()
  const { session } = useAuth()
  const queryClient = useQueryClient()
  const status = useQuery({
    queryKey: ['mfa'],
    queryFn: () => session.api.get<MfaStatus>('/auth/admin/mfa'),
  })

  const [setup, setSetup] = useState<{ secret: string; otpauthUrl: string } | null>(null)
  const [qr, setQr] = useState<string | null>(null)
  const [code, setCode] = useState('')
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null)
  const [disableOpen, setDisableOpen] = useState(false)

  useEffect(() => {
    if (!setup) return
    let cancelled = false
    // QR généré localement (aucun service tiers ne voit le secret).
    void QRCode.toDataURL(setup.otpauthUrl, {
      margin: 1,
      width: 200,
      errorCorrectionLevel: 'M',
    }).then((url) => {
      if (!cancelled) setQr(url)
    })
    return () => {
      cancelled = true
    }
  }, [setup])

  const begin = useMutation({
    mutationFn: () =>
      session.api.post<{ secret: string; otpauthUrl: string }>('/auth/admin/mfa/setup'),
    onSuccess: (data) => {
      setQr(null)
      setSetup(data)
    },
  })

  const confirm = useMutation({
    mutationFn: (value: string) =>
      session.api.post<{ recoveryCodes: string[] }>('/auth/admin/mfa/confirm', { code: value }),
    onSuccess: async (data) => {
      setSetup(null)
      setCode('')
      setRecoveryCodes(data.recoveryCodes)
      await session.reloadUser()
      await queryClient.invalidateQueries({ queryKey: ['mfa'] })
      await queryClient.invalidateQueries({ queryKey: MY_PERMISSIONS_KEY })
    },
    onError: () => setCode(''),
  })

  const enabled = status.data?.enabled ?? false

  return (
    <Card>
      <CardHeader
        title={t('account.mfa.title')}
        actions={
          status.data && (
            <Badge variant={enabled ? 'success' : 'neutral'} dot>
              {enabled ? t('account.mfa.enabled') : t('account.mfa.disabled')}
            </Badge>
          )
        }
      />
      <CardBody>
        <div className="stack">
          <p className="text-muted">{t('account.mfa.explain')}</p>
          {status.data?.required && !enabled && (
            <Alert tone="warning">{t('account.mfa.required')}</Alert>
          )}

          {enabled && !recoveryCodes && (
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <span className="text-muted" style={{ fontSize: 'var(--text-sm)' }}>
                {t('account.mfa.remaining', { count: status.data?.recoveryCodesRemaining ?? 0 })}
              </span>
              <Button variant="ghost" icon={<ShieldOff />} onClick={() => setDisableOpen(true)}>
                {t('account.mfa.disable')}
              </Button>
            </div>
          )}

          {!enabled && !setup && !recoveryCodes && (
            <div>
              <Button
                variant="primary"
                icon={<ShieldCheck />}
                loading={begin.isPending}
                onClick={() => begin.mutate()}
              >
                {t('account.mfa.enable')}
              </Button>
            </div>
          )}

          {setup && (
            <div className="stack">
              <p>{t('account.mfa.step1')}</p>
              <div className="row" style={{ alignItems: 'flex-start', gap: 'var(--space-6)' }}>
                <div
                  className="card"
                  style={{ padding: 'var(--space-2)', background: 'var(--card)' }}
                >
                  {qr ? (
                    <img src={qr} width={200} height={200} alt="QR code de configuration" />
                  ) : (
                    <span className="skeleton" style={{ width: 200, height: 200 }} />
                  )}
                </div>
                <div className="stack" style={{ flex: 1, minWidth: 220 }}>
                  <p className="text-muted" style={{ fontSize: 'var(--text-sm)' }}>
                    {t('account.mfa.manual')}
                  </p>
                  <code className="matricule" style={{ wordBreak: 'break-all' }}>
                    {setup.secret.match(/.{1,4}/g)?.join(' ')}
                  </code>
                </div>
              </div>
              <p>{t('account.mfa.step2')}</p>
              {confirm.isError && <Alert tone="danger">{errorMessage(confirm.error)}</Alert>}
              <OtpInput
                value={code}
                onChange={setCode}
                onComplete={(value) => confirm.mutate(value)}
                invalid={confirm.isError}
                disabled={confirm.isPending}
              />
              <div className="row">
                <Button
                  variant="primary"
                  loading={confirm.isPending}
                  disabled={code.length !== 6}
                  onClick={() => confirm.mutate(code)}
                >
                  {t('account.mfa.confirm')}
                </Button>
                <Button variant="ghost" onClick={() => setSetup(null)}>
                  {t('account.mfa.cancel')}
                </Button>
              </div>
            </div>
          )}

          {recoveryCodes && (
            <RecoveryCodes codes={recoveryCodes} onDone={() => setRecoveryCodes(null)} />
          )}
        </div>
      </CardBody>
      <DisableMfaModal open={disableOpen} onClose={() => setDisableOpen(false)} />
    </Card>
  )
}

function RecoveryCodes({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)
  const text = codes.join('\n')

  return (
    <div className="stack">
      <Alert tone="warning" title={t('account.mfa.codesTitle')}>
        {t('account.mfa.codesHelp')}
      </Alert>
      <div className="code-grid" aria-label={t('account.mfa.codesTitle')}>
        {codes.map((code) => (
          <span key={code}>{code}</span>
        ))}
      </div>
      <div className="row">
        <Button
          icon={<KeyRound />}
          onClick={async () => {
            await navigator.clipboard.writeText(text)
            setCopied(true)
          }}
        >
          {copied ? t('account.mfa.copied') : t('account.mfa.copy')}
        </Button>
        <a
          className="btn btn-secondary"
          href={`data:text/plain;charset=utf-8,${encodeURIComponent(text)}`}
          download="codes-de-secours-concours.txt"
        >
          <Download aria-hidden="true" />
          {t('account.mfa.download')}
        </a>
        <Button variant="primary" onClick={onDone}>
          {t('account.mfa.done')}
        </Button>
      </div>
    </div>
  )
}

function DisableMfaModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation()
  const { session } = useAuth()
  const queryClient = useQueryClient()
  const toast = useToast()
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')

  const disable = useMutation({
    mutationFn: () => session.api.post('/auth/admin/mfa/disable', { password, code }),
    onSuccess: async () => {
      await session.reloadUser()
      await queryClient.invalidateQueries({ queryKey: ['mfa'] })
      await queryClient.invalidateQueries({ queryKey: MY_PERMISSIONS_KEY })
      toast.success(t('account.mfa.disabled'))
      setPassword('')
      setCode('')
      onClose()
    },
  })

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('account.mfa.disableTitle')}
      description={t('account.mfa.disableHelp')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('account.mfa.cancel')}
          </Button>
          <Button
            variant="danger"
            loading={disable.isPending}
            disabled={!password || code.length < 6}
            onClick={() => disable.mutate()}
          >
            {t('account.mfa.disable')}
          </Button>
        </>
      }
    >
      <div className="stack">
        {disable.isError && <Alert tone="danger">{errorMessage(disable.error)}</Alert>}
        <PasswordInput
          label={t('auth.password')}
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <Input
          label={t('account.mfa.code')}
          mono
          autoComplete="one-time-code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
        />
      </div>
    </Modal>
  )
}

/* ------------------------------------------------------------------ */
/* Mot de passe et sessions                                            */
/* ------------------------------------------------------------------ */

function ChangePasswordCard({ email }: { email: string }) {
  const { t } = useTranslation()
  const { session } = useAuth()
  const toast = useToast()
  const [current, setCurrent] = useState('')
  const [password, setPassword] = useState('')
  const [confirmation, setConfirmation] = useState('')

  const change = useMutation({
    mutationFn: () =>
      session.api.post<LoginResponse>('/auth/admin/password/change', {
        currentPassword: current,
        password,
        passwordConfirmation: confirmation,
      }),
    onSuccess: (response) => {
      session.acceptSession(response)
      setCurrent('')
      setPassword('')
      setConfirmation('')
      toast.success(t('account.password.done'))
    },
  })

  const mismatch = confirmation.length > 0 && confirmation !== password
  const ok = current && passwordIssues(password, email).length === 0 && !mismatch && confirmation

  function submit(event: FormEvent) {
    event.preventDefault()
    if (ok) change.mutate()
  }

  return (
    <Card>
      <CardHeader title={t('account.password.title')} />
      <CardBody>
        <form className="stack" style={{ maxWidth: 480 }} onSubmit={submit} noValidate>
          {change.isError && <Alert tone="danger">{errorMessage(change.error)}</Alert>}
          <input type="email" autoComplete="username" value={email} readOnly hidden />
          <PasswordInput
            label={t('account.password.current')}
            autoComplete="current-password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
          />
          <PasswordInput
            label={t('account.password.new')}
            autoComplete="new-password"
            showRules
            email={email}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={fieldErrors(change.error).password}
          />
          <PasswordInput
            label={t('account.password.confirm')}
            autoComplete="new-password"
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
            error={mismatch ? t('auth.mismatch') : undefined}
          />
          <div>
            <Button type="submit" variant="primary" loading={change.isPending} disabled={!ok}>
              {t('account.password.submit')}
            </Button>
          </div>
        </form>
      </CardBody>
    </Card>
  )
}

function SessionsCard() {
  const { t } = useTranslation()
  const { session } = useAuth()
  const navigate = useNavigate()
  const logoutAll = useMutation({
    mutationFn: () => session.logoutEverywhere(),
    onSettled: () => navigate('/connexion', { replace: true }),
  })
  return (
    <Card>
      <CardHeader title={t('account.sessions.title')} />
      <CardBody>
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <p className="text-muted">{t('account.sessions.help')}</p>
          <Button
            icon={<LogOut />}
            loading={logoutAll.isPending}
            onClick={() => logoutAll.mutate()}
          >
            {t('account.sessions.logoutAll')}
          </Button>
        </div>
      </CardBody>
    </Card>
  )
}
